<script lang="ts" setup>
import type { MenuItem } from 'primevue/menuitem'
import { pageScene } from '~/utils/workspaceScenes'

const route = useRoute()

const { layoutState, setActiveMenuItem, toggleMenu } = useTemplateLayout()

const props = defineProps({
  item: {
    type: Object,
    default: () => ({}),
  },
  index: {
    type: Number,
    default: 0,
  },
  root: {
    type: Boolean,
    default: true,
  },
  parentItemKey: {
    type: String,
    default: null,
  },
})

const isActiveMenu = ref<boolean>(false)
const itemKey = ref()

onBeforeMount(() => {
  itemKey.value = props.parentItemKey
    ? props.parentItemKey + '-' + props.index
    : String(props.index)

  const activeItem = layoutState.value.activeMenuItem

  isActiveMenu.value =
    activeItem === itemKey.value || activeItem
      ? activeItem?.startsWith(itemKey.value + '-')
      : false
})

watch(
  () => layoutState.value.activeMenuItem,
  (newVal) => {
    isActiveMenu.value =
      newVal === itemKey.value || newVal?.startsWith(itemKey.value + '-')
  }
)

function itemClick(event: Event, item: MenuItem) {
  if (item.disabled) {
    event.preventDefault()
    return
  }

  if (
    (item.to || item.url) &&
    (layoutState.value.staticMenuMobileActive ||
      layoutState.value.overlayMenuActive)
  ) {
    toggleMenu()
  }

  if (item.command) {
    item.command({ originalEvent: event, item: item })
  }

  // Clicking the page you are already on: let it refresh itself (e.g. Inquiry downloads new calls).
  if (
    typeof item.to === 'string' &&
    item.to.replace(/\/$/, '') === route.path.replace(/\/$/, '')
  ) {
    window.dispatchEvent(new CustomEvent('menu-reselect', { detail: item.to }))
  }

  const foundItemKey = item.items
    ? isActiveMenu.value
      ? props.parentItemKey
      : itemKey
    : itemKey.value

  setActiveMenuItem(foundItemKey)
}

const activeMenuTo = inject<ComputedRef<string | undefined> | null>(
  'activeMenuTo',
  null
)

function checkActiveRoute(item: MenuItem) {
  if (activeMenuTo) return activeMenuTo.value === item.to
  return route.path === item.to || route.path.startsWith(item.to + '/')
}
</script>

<template>
  <li
    :class="{ 'layout-root-menuitem': root, 'active-menuitem': isActiveMenu }"
  >
    <div
      v-if="root && item.visible !== false"
      class="layout-menuitem-root-text"
    >
      {{ item.label }}
    </div>
    <a
      v-if="(!item.to || item.items) && item.visible !== false"
      :href="item.url"
      @click="itemClick($event, item)"
      :class="item.class"
      :target="item.target"
      tabindex="0"
    >
      <i :class="item.icon" class="layout-menuitem-icon"></i>
      <span class="layout-menuitem-text">{{ item.label }}</span>
      <i
        class="pi pi-fw pi-angle-down layout-submenu-toggler"
        v-if="item.items"
      />
      <Badge
        v-if="item.badge"
        class="ml-auto"
        :severity="item.badgeSeverity"
        :value="item.badge"
      />
    </a>
    <NuxtLink
      v-if="item.to && !item.items && item.visible !== false"
      :class="[item.class, { 'active-route': checkActiveRoute(item) }]"
      :to="item.to"
      :data-scene="typeof item.to === 'string' ? pageScene(item.to) : undefined"
      tabindex="0"
      @click="itemClick($event, item)"
    >
      <i :class="item.icon" class="layout-menuitem-icon"></i>
      <span class="layout-menuitem-text">{{ item.label }}</span>
      <i
        class="pi pi-fw pi-angle-down layout-submenu-toggler"
        v-if="item.items"
      />
      <Badge
        v-if="item.badge"
        :severity="item.badgeSeverity"
        :value="item.badge"
        class="ml-auto"
      />
    </NuxtLink>
    <Transition
      v-if="item.items && item.visible !== false"
      name="layout-submenu"
    >
      <ul v-show="root ? true : isActiveMenu" class="layout-submenu">
        <app-menu-item
          v-for="(child, i) in item.items"
          :key="child"
          :index="i as number"
          :item="child"
          :parentItemKey="itemKey"
          :root="false"
        ></app-menu-item>
      </ul>
    </Transition>
  </li>
</template>
